import { SiteUrls } from '@tableau/shared-urls';
import { IPromise } from 'angular';
import * as React from 'react';

import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, TextFieldWidget } from '@tableau/widgets';
import { Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { showActionableToast } from '../../../common/react/components/ActionableMessage';
import { Location } from '../../../common/react/Location';
import { CollectionsErrorCode } from '../../../ts/helpers/ErrorCodes';
import { ModalInstance, ReactModalOptions, ReactModalService } from '../../../ts/services/ReactModalService';
import { ToastType } from '../../../ts/services/ToasterService';
import { CollectionsResourceQuery } from './Collections';
import { ICollectionInfo } from './ICollectionInfo';

const DIALOG_WIDTH = 400;

export namespace CreateCollectionDialog {
  export interface ContentsProps {
    readonly close: () => void;
    readonly siteUrls: SiteUrls;
  }

  export interface ContentsState {
    readonly name: string;
    readonly message: string;
  }

  export class Contents extends React.PureComponent<ContentsProps, ContentsState> {
    private initialNameState: string = '';

    constructor(props: ContentsProps) {
      super(props);
      this.state = { name: this.initialNameState, message: undefined };
    }

    public render(): JSX.Element {
      return (
        <div>
          <TextFieldWidget
            kind={'outline'}
            label={Messages.name()}
            message={this.getErrorMessage()}
            onChange={(e) => {
              this.setState({ name: e.target.value, message: undefined });
            }}
            onClear={() => {
              this.setState({ name: this.initialNameState });
            }}
            placeholder={Messages.collectionNamePlaceholder()}
            style={{ width: '330px' }}
            testId={'collection-name'}
            valid={this.isValid()}
          />
          {this.buttonRow()}
        </div>
      );
    }

    private getErrorMessage(): string {
      if (this.state.message) {
        return this.state.message;
      }

      if (!this.state.name || this.state.name === this.initialNameState) {
        return undefined;
      }

      if (this.state.name.trim().length === 0) {
        return Messages.createCollectionNameWhitespaceError();
      } else if (this.state.name.length > 255) {
        return Messages.createCollectionNameTooLongError();
      }

      return undefined;
    }

    private isValid(): boolean | 'pending' {
      return this.getErrorMessage() === undefined;
    }

    private buttonRow(): JSX.Element {
      return (
        <div style={{ marginTop: 30, width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonWidget
            buttonType={ButtonType.Go}
            testId={'create-collection'}
            disabled={!this.state.name || this.state.name === this.initialNameState || !this.isValid()}
            onClick={this.handleCreateButtonClick}
          >
            {Messages.create()}
          </ButtonWidget>
        </div>
      );
    }

    private handleSuccessfulCreation(collectionInfo: ICollectionInfo) {
      this.props.close();

      showActionableToast({
          testId: 'collection-creation-success',
          message: Messages.createCollectionSuccess({ COLLECTION_NAME: this.state.name }),
          actionMessage: Messages.goToCollection(),
          action: () => { Location.instance.setUrl(this.props.siteUrls.collectionUrl(collectionInfo.id)); }
        },
        ToastType.Success);
    }

    private handleCreateButtonClick = (): void => {
      (new CollectionsResourceQuery()).create(this.state.name)
        .then((collectionInfo: ICollectionInfo) => {
          this.handleSuccessfulCreation(collectionInfo);
        })
        .catch((result) => {
          const errorCode = result.data.errorCode;
          let message = Messages.errorUnknown();
          if (errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
            message = Messages.createCollectionNameDuplicateError();
          }
          else if (errorCode === CollectionsErrorCode.INVALID_NAME_NULL || errorCode === CollectionsErrorCode.INVALID_NAME_WHITESPACE) {
            message = Messages.createCollectionNameWhitespaceError();
          }
          else if (errorCode === CollectionsErrorCode.INVALID_NAME_TOO_LONG) {
            message = Messages.createCollectionNameTooLongError();
          }
          this.setState({ message: message });
        });
    }
  }

  export function Options(closeDialog: () => void, urls: SiteUrls): ReactModalOptions {
    return {
      title: Messages.createCollectionDialogTitle(),
      testId: 'create-collection-dialog',
      type: DialogType.LightweightTitleBar,
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogWidth: DIALOG_WIDTH,
      dialogBodyStyle: { maxWidth: DIALOG_WIDTH + 'px' },
      dialogContent: <Contents close={closeDialog} siteUrls={urls}/>,
      dialogContentStyle: { padding: CommonStyles.Sizes.MediumSpace },
    };
  }

  export function open(urls: SiteUrls): IPromise<any> {
    let modalInstance: ModalInstance;

    const closeDialog = () => modalInstance && modalInstance.close(false);

    modalInstance = ReactModalService.open(Options(closeDialog, urls));
    return modalInstance.result;
  }
}
